import { HolderOutlined } from '@ant-design/icons';
import { Button, Empty, Input, Tooltip } from 'antd';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { SortableEvent } from 'sortablejs';
import type Sortable from 'sortablejs';
import type {
  DecryptedFileSecretData,
  FileSecretField as FileSecretFieldType,
  SecretFileGroup,
  SecretFilePresetType,
} from '../../../interfaces/FileSecret';
import { createSortableList } from '../../../utils/createSortableList';
import {
  createCustomField,
  createWebsiteField,
  resolveSecretFilePreset,
} from '../../../utils/fileSecretPresets';
import { Icon } from '../../Core/Icon';
import { FileSecretField } from '../FileSecretField';

export type FileViewerSecretEditorHandle = {
  save: () => void;
  /** Reset local edits and leave edit mode (does not close parent). */
  discard: () => void;
};

interface Props {
  mode: 'view' | 'edit';
  initialData?: DecryptedFileSecretData;
  onSave?: (
    data: DecryptedFileSecretData,
    newSecretKey?: string,
    oldSecretKey?: string,
    hint?: string,
  ) => void;
  onChange?: (data: DecryptedFileSecretData) => void;
  onCancel?: () => void;
  onModeChange?: (mode: 'view' | 'edit') => void;
  showHeader?: boolean;
  /** When false, hide Edit/Save/Cancel in header (parent owns footer actions). */
  showHeaderActions?: boolean;
  /** When false, hide the field search strip. */
  showFieldSearch?: boolean;
  /** Autofocus first field value (custom create modal). */
  autoFocusFirstField?: boolean;
  isSaving?: boolean;
  isEncryptedFile?: boolean;
}

const generateId = () => crypto.randomUUID();

/** Droppable id for the ungrouped fields zone (custom preset). */
const UNGROUPED_CONTAINER_ID = 'secret-container-ungrouped';

function groupContainerId(groupId: string): string {
  return `secret-container-${groupId}`;
}

/** null = ungrouped; string = groupId. */
type FieldContainerKey = string | null;

function fieldContainerKey(field: FileSecretFieldType): FieldContainerKey {
  return field.groupId ?? null;
}

function withContainerGroupId(
  field: FileSecretFieldType,
  container: FieldContainerKey,
): FileSecretFieldType {
  if (container === null) {
    const { groupId: _removed, ...rest } = field;
    return rest;
  }
  return { ...field, groupId: container };
}

function fieldMatchesQuery(field: FileSecretFieldType, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    field.label.toLowerCase().includes(q) ||
    field.value.toLowerCase().includes(q) ||
    String(field.type).toLowerCase().includes(q) ||
    String(field.key ?? '')
      .toLowerCase()
      .includes(q)
  );
}

function sortByOrder<T extends { order?: number }>(items: T[]): T[] {
  return [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

function reindexOrders<T extends { order?: number }>(items: T[]): T[] {
  return items.map((item, index) => ({ ...item, order: index }));
}

/** Max order among top-level items (ungrouped fields + groups). Seed -1 when empty. */
function maxTopLevelOrder(
  fields: readonly FileSecretFieldType[],
  groupList: readonly SecretFileGroup[],
): number {
  const ungroupedMax = fields
    .filter((f) => !f.groupId)
    .reduce((max, f) => Math.max(max, f.order ?? 0), -1);
  const groupMax = groupList.reduce((max, g) => Math.max(max, g.order ?? 0), -1);
  return Math.max(ungroupedMax, groupMax);
}

function parentKeyFromList(list: HTMLElement): FieldContainerKey {
  const raw = list.dataset.groupParent;
  if (raw === undefined || raw === '') return null;
  return raw;
}

function readDomFieldOrder(root: HTMLElement): {
  ids: string[];
  groups: Map<string, FieldContainerKey>;
} {
  const ids: string[] = [];
  const groups = new Map<string, FieldContainerKey>();

  for (const list of root.querySelectorAll('[data-field-list]')) {
    if (!(list instanceof HTMLElement)) continue;
    const parent = parentKeyFromList(list);
    for (const child of Array.from(list.children)) {
      if (!(child instanceof HTMLElement)) continue;
      if (child.dataset.sortableKind !== 'field') continue;
      const id = child.dataset.sortableId;
      if (!id) continue;
      ids.push(id);
      groups.set(id, parent);
    }
  }

  return { ids, groups };
}

function restoreFieldDomToStart(
  root: HTMLElement,
  orderIds: readonly string[],
  groups: Map<string, FieldContainerKey>,
): void {
  const items = new Map<string, HTMLElement>();

  for (const list of root.querySelectorAll('[data-field-list]')) {
    if (!(list instanceof HTMLElement)) continue;
    for (const child of Array.from(list.children)) {
      if (!(child instanceof HTMLElement) || child.dataset.sortableKind !== 'field') continue;
      const id = child.dataset.sortableId;
      if (id) items.set(id, child);
    }
  }

  for (const list of root.querySelectorAll('[data-field-list]')) {
    if (!(list instanceof HTMLElement)) continue;
    for (const child of Array.from(list.children)) {
      if (child instanceof HTMLElement && child.dataset.sortableKind === 'field') {
        child.remove();
      }
    }
  }

  const listForParent = (parent: FieldContainerKey): HTMLElement | null => {
    if (parent === null) {
      return root.querySelector('[data-field-list][data-group-parent=""]');
    }
    return root.querySelector(`[data-field-list][data-group-parent="${CSS.escape(parent)}"]`);
  };

  for (const id of orderIds) {
    const el = items.get(id);
    if (!el) continue;
    const list = listForParent(groups.get(id) ?? null);
    list?.appendChild(el);
  }
}

function restoreGroupDomToStart(groupList: HTMLElement, orderIds: readonly string[]): void {
  const items = new Map<string, HTMLElement>();
  for (const child of Array.from(groupList.children)) {
    if (!(child instanceof HTMLElement) || child.dataset.sortableKind !== 'group') continue;
    const id = child.dataset.sortableId;
    if (id) items.set(id, child);
  }
  for (const child of Array.from(groupList.children)) {
    if (child instanceof HTMLElement && child.dataset.sortableKind === 'group') {
      child.remove();
    }
  }
  for (const id of orderIds) {
    const el = items.get(id);
    if (el) groupList.appendChild(el);
  }
}

interface DragHandleProps {
  ariaLabel: string;
  testId: string;
  handleClassName: string;
}

const DragHandle = ({ ariaLabel, testId, handleClassName }: DragHandleProps) => (
  <Tooltip title={ariaLabel}>
    <Button
      type="text"
      size="small"
      className={`mt-0.5 shrink-0 cursor-grab! px-1! ${handleClassName}`}
      icon={<HolderOutlined />}
      aria-label={ariaLabel}
      data-testid={testId}
      onClick={(e) => e.stopPropagation()}
    />
  </Tooltip>
);

/** Group title flush-left; fields sit indented underneath for hierarchy. */
const GroupPanelShell = ({
  group,
  dragHandle,
  header,
  children,
  onActivate,
}: {
  group: SecretFileGroup;
  dragHandle: ReactNode;
  header: ReactNode;
  children: ReactNode;
  onActivate: () => void;
}) => (
  <div
    className="bg-gray-medium/30 rounded-lg py-2 pr-3 pl-1.5"
    data-testid={`secret-group-${group.id}`}
    data-group-key={group.key}
    data-group-locked={group.locked ? 'true' : 'false'}
    data-sortable-id={group.id}
    data-sortable-kind="group"
    data-sortable-locked={group.locked ? 'true' : 'false'}
    onClick={onActivate}
  >
    <div className="mb-1.5 flex min-h-7 items-center gap-0.5">
      {dragHandle}
      {header}
    </div>
    <div className="pl-6">{children}</div>
  </div>
);

export const FileViewerSecretEditor = forwardRef<FileViewerSecretEditorHandle, Props>(
  function FileViewerSecretEditor(
    {
      mode,
      initialData,
      onSave,
      onChange,
      onCancel,
      onModeChange,
      showHeader = true,
      showHeaderActions = true,
      showFieldSearch = true,
      autoFocusFirstField = false,
      isSaving = false,
      isEncryptedFile = false,
    },
    ref,
  ) {
    const { t } = useTranslation();

    const [name, setName] = useState(initialData?.name || '');
    const [fields, setFields] = useState<FileSecretFieldType[]>(() =>
      sortByOrder(initialData?.fields ?? []),
    );
    const [groups, setGroups] = useState<SecretFileGroup[]>(() =>
      sortByOrder(initialData?.groups ?? []),
    );
    const [preset] = useState<SecretFilePresetType | string>(() => initialData?.preset ?? 'custom');
    const [hint] = useState(initialData?.hint);
    const [schemaVersion] = useState(initialData?.schemaVersion ?? 2);
    const [fieldSearch, setFieldSearch] = useState('');
    const [isChangingKey, setIsChangingKey] = useState(false);
    const [oldSecretKey, setOldSecretKey] = useState('');
    const [newSecretKey, setNewSecretKey] = useState('');
    const [confirmSecretKey, setConfirmSecretKey] = useState('');
    const [newHint, setNewHint] = useState('');
    const [activeGroupId, setActiveGroupId] = useState<string | undefined>(
      () => initialData?.groups?.[0]?.id,
    );
    const [draggingGroup, setDraggingGroup] = useState(false);
    const [sortableEpoch, setSortableEpoch] = useState(0);

    const rootRef = useRef<HTMLDivElement>(null);
    const fieldsRef = useRef(fields);
    const groupsRef = useRef(groups);
    const draggingRef = useRef(false);
    const fieldDragStartRef = useRef<{
      orderIds: string[];
      groups: Map<string, FieldContainerKey>;
    }>({ orderIds: [], groups: new Map() });
    const groupDragStartRef = useRef<string[]>([]);

    useEffect(() => {
      fieldsRef.current = fields;
    }, [fields]);
    useEffect(() => {
      groupsRef.current = groups;
    }, [groups]);

    const isReadOnly = mode === 'view';
    const resolvedPreset = resolveSecretFilePreset(preset);
    const isCredentials = resolvedPreset === 'credentials';
    const isCustom = resolvedPreset === 'custom';
    const fieldDragUnlocked = !fieldSearch.trim() && !draggingGroup;

    const buildData = useCallback(
      (nextName: string, nextFields: FileSecretFieldType[], nextGroups: SecretFileGroup[]) => {
        const data: DecryptedFileSecretData = {
          schemaVersion,
          name: nextName,
          encrypted: initialData?.encrypted || false,
          fields: nextFields,
          groups: nextGroups,
          preset,
          ...(hint ? { hint } : {}),
        };
        return data;
      },
      [schemaVersion, initialData?.encrypted, preset, hint],
    );

    const notifyChange = useCallback(
      (nextName: string, nextFields: FileSecretFieldType[], nextGroups: SecretFileGroup[]) => {
        onChange?.(buildData(nextName, nextFields, nextGroups));
      },
      [onChange, buildData],
    );

    const handleNameChange = useCallback(
      (newName: string) => {
        setName(newName);
        notifyChange(newName, fields, groups);
      },
      [fields, groups, notifyChange],
    );

    const handleFieldChange = useCallback(
      (index: number, updatedField: FileSecretFieldType) => {
        setFields((prev) => {
          const newFields = [...prev];
          newFields[index] = updatedField;
          notifyChange(name, newFields, groups);
          return newFields;
        });
      },
      [name, groups, notifyChange],
    );

    const handleRemoveField = useCallback(
      (index: number) => {
        setFields((prev) => {
          const target = prev[index];
          if (target?.locked) return prev;
          if (target?.key === 'website' && prev.filter((f) => f.key === 'website').length <= 1) {
            return prev;
          }
          const newFields = prev.filter((_, i) => i !== index);
          notifyChange(name, newFields, groups);
          return newFields;
        });
      },
      [name, groups, notifyChange],
    );

    const credentialsAutofillGroupId = useMemo(
      () => (isCredentials ? groups.find((g) => g.key === 'credentials_autofill')?.id : undefined),
      [isCredentials, groups],
    );
    const customFieldsGroupId = useMemo(
      () => (!isCustom ? groups.find((g) => g.key === 'custom_fields')?.id : undefined),
      [isCustom, groups],
    );

    const targetGroupId = activeGroupId ?? groups[0]?.id;

    const addFieldToGroup = useCallback(
      (groupId: string) => {
        setActiveGroupId(groupId);
        setFields((prev) => {
          const maxOrder = prev
            .filter((f) => f.groupId === groupId)
            .reduce((max, f) => Math.max(max, f.order ?? 0), -1);
          const newFields = [...prev, createCustomField(groupId, maxOrder + 1, t)];
          notifyChange(name, newFields, groups);
          return newFields;
        });
      },
      [name, groups, notifyChange, t],
    );

    const addUngroupedField = useCallback(() => {
      setActiveGroupId(undefined);
      setFields((prev) => {
        // Shared top-level sequence with groups so outer Add lands after last group too.
        const newFields = [
          ...prev,
          createCustomField(undefined, maxTopLevelOrder(prev, groups) + 1, t),
        ];
        notifyChange(name, newFields, groups);
        return newFields;
      });
    }, [name, groups, notifyChange, t]);

    const handleAddField = useCallback(() => {
      // Non-custom presets: extras land in Custom fields.
      // Custom outer bar: always ungrouped (inner group buttons call addFieldToGroup).
      if (!isCustom) {
        if (!customFieldsGroupId) return;
        addFieldToGroup(customFieldsGroupId);
        return;
      }
      addUngroupedField();
    }, [isCustom, customFieldsGroupId, addFieldToGroup, addUngroupedField]);

    const handleAddWebsite = useCallback(() => {
      const groupId = isCredentials ? credentialsAutofillGroupId : targetGroupId;
      if (!groupId) return;
      setFields((prev) => {
        const maxOrder = prev
          .filter((f) => f.groupId === groupId)
          .reduce((max, f) => Math.max(max, f.order ?? 0), -1);
        const newFields = [...prev, createWebsiteField(groupId, maxOrder + 1, t)];
        notifyChange(name, newFields, groups);
        return newFields;
      });
      if (isCredentials && credentialsAutofillGroupId) {
        setActiveGroupId(credentialsAutofillGroupId);
      }
    }, [isCredentials, credentialsAutofillGroupId, targetGroupId, name, groups, notifyChange, t]);

    const handleAddGroup = useCallback(() => {
      if (!isCustom) return;
      const newGroup: SecretFileGroup = {
        id: generateId(),
        key: 'custom_fields',
        label: t('secret.newGroup'),
        order: maxTopLevelOrder(fields, groups) + 1,
        locked: false,
      };
      const nextGroups = sortByOrder([...groups, newGroup]);
      setGroups(nextGroups);
      setActiveGroupId(newGroup.id);
      notifyChange(name, fields, nextGroups);
    }, [isCustom, groups, name, fields, notifyChange, t]);

    const handleRenameGroup = useCallback(
      (groupId: string, label: string) => {
        const group = groups.find((g) => g.id === groupId);
        if (!group || group.locked || !isCustom) return;
        const nextGroups = groups.map((g) => (g.id === groupId ? { ...g, label } : g));
        setGroups(nextGroups);
        notifyChange(name, fields, nextGroups);
      },
      [groups, isCustom, name, fields, notifyChange],
    );

    const handleDeleteGroup = useCallback(
      (groupId: string) => {
        if (!isCustom) return;
        const group = groups.find((g) => g.id === groupId);
        if (!group || group.locked) return;
        if (fields.some((f) => f.groupId === groupId)) return;
        const nextGroups = reindexOrders(sortByOrder(groups.filter((g) => g.id !== groupId)));
        setGroups(nextGroups);
        if (activeGroupId === groupId) {
          setActiveGroupId(nextGroups[0]?.id);
        }
        notifyChange(name, fields, nextGroups);
      },
      [isCustom, groups, fields, activeGroupId, name, notifyChange],
    );

    const canReorderFieldInGroup = useCallback(
      (field: FileSecretFieldType, group: SecretFileGroup | undefined) => {
        if (field.locked) return false;
        if (isCustom) return true;
        return group?.key === 'custom_fields';
      },
      [isCustom],
    );

    const canReorderGroup = useCallback(
      (group: SecretFileGroup) => !isReadOnly && isCustom && !group.locked,
      [isCustom, isReadOnly],
    );

    const commitFieldsFromDom = useCallback(
      (root: HTMLElement, instances: Sortable[]) => {
        const start = fieldDragStartRef.current;
        const { ids, groups: nextGroupsMap } = readDomFieldOrder(root);

        const byId = new Map(fieldsRef.current.map((f) => [f.id, f]));
        const ordered = ids.map((id) => byId.get(id)).filter((f): f is FileSecretFieldType => !!f);

        if (ordered.length !== fieldsRef.current.length) {
          draggingRef.current = false;
          setDraggingGroup(false);
          for (const instance of instances) instance.destroy();
          restoreFieldDomToStart(root, start.orderIds, start.groups);
          setSortableEpoch((e) => e + 1);
          return;
        }

        // Apply container membership + per-container order.
        const byContainer = new Map<string, FileSecretFieldType[]>();
        const containerKey = (key: FieldContainerKey) => (key === null ? '' : key);

        for (const id of ids) {
          const field = byId.get(id);
          if (!field) continue;
          const toKey = nextGroupsMap.has(id)
            ? (nextGroupsMap.get(id) ?? null)
            : fieldContainerKey(field);
          const fromKey = fieldContainerKey(field);
          const fromGroup = fromKey ? groupsRef.current.find((g) => g.id === fromKey) : undefined;
          let finalKey = toKey;
          if (!canReorderFieldInGroup(field, fromGroup)) {
            // Locked / non-reorderable: keep original container.
            finalKey = fromKey;
          }
          const bucket = containerKey(finalKey);
          const list = byContainer.get(bucket) ?? [];
          list.push(withContainerGroupId(field, finalKey));
          byContainer.set(bucket, list);
        }

        const updatedById = new Map<string, FileSecretFieldType>();
        for (const [, peers] of byContainer) {
          for (const f of reindexOrders(peers)) {
            updatedById.set(f.id, f);
          }
        }

        const nextFields = fieldsRef.current.map((f) => updatedById.get(f.id) ?? f);

        for (const instance of instances) instance.destroy();
        restoreFieldDomToStart(root, start.orderIds, start.groups);

        fieldsRef.current = nextFields;
        setFields(nextFields);
        notifyChange(name, nextFields, groupsRef.current);
        draggingRef.current = false;
        setDraggingGroup(false);
        setSortableEpoch((e) => e + 1);
      },
      [canReorderFieldInGroup, name, notifyChange],
    );

    const commitGroupsFromDom = useCallback(
      (groupList: HTMLElement, instance: Sortable) => {
        const startIds = groupDragStartRef.current;
        const nextIds: string[] = [];
        for (const child of Array.from(groupList.children)) {
          if (!(child instanceof HTMLElement) || child.dataset.sortableKind !== 'group') continue;
          const id = child.dataset.sortableId;
          if (id) nextIds.push(id);
        }

        instance.destroy();
        restoreGroupDomToStart(groupList, startIds);

        if (nextIds.length !== startIds.length || nextIds.join('|') === startIds.join('|')) {
          draggingRef.current = false;
          setDraggingGroup(false);
          setSortableEpoch((e) => e + 1);
          return;
        }

        const byId = new Map(groupsRef.current.map((g) => [g.id, g]));
        const reordered = nextIds
          .map((id) => byId.get(id))
          .filter((g): g is SecretFileGroup => !!g)
          .filter((g) => !g.locked || true);

        // Locked groups keep relative positions only if all unlocked moved among themselves —
        // Sortable filter already blocks dragging locked; accept DOM order.
        const nextGroups = reindexOrders(reordered);
        groupsRef.current = nextGroups;
        setGroups(nextGroups);
        notifyChange(name, fieldsRef.current, nextGroups);
        draggingRef.current = false;
        setDraggingGroup(false);
        setSortableEpoch((e) => e + 1);
      },
      [name, notifyChange],
    );

    // Nested field lists (custom: shared group; non-custom: isolated per list).
    useEffect(() => {
      const root = rootRef.current;
      if (!root || isReadOnly || !fieldDragUnlocked) return;

      const lists = Array.from(root.querySelectorAll<HTMLElement>('[data-field-list]'));
      if (lists.length === 0) return;

      const anyDraggable = lists.some((list) =>
        Array.from(list.children).some(
          (child) =>
            child instanceof HTMLElement &&
            child.dataset.sortableKind === 'field' &&
            child.dataset.sortableLocked !== 'true',
        ),
      );
      if (!anyDraggable) return;

      const instances: Sortable[] = [];
      const sharedGroup = isCustom ? 'secret-fields' : undefined;

      for (const list of lists) {
        instances.push(
          createSortableList(list, {
            group: sharedGroup,
            handle: '.secret-field-drag-handle',
            draggable: '[data-sortable-kind="field"]',
            filter: '[data-sortable-locked="true"]',
            ghostClass: 'sortable-ghost',
            chosenClass: 'sortable-chosen',
            dragClass: 'sortable-drag',
            fallbackClass: 'sortable-fallback',
            emptyInsertThreshold: 40,
            onStart: () => {
              draggingRef.current = true;
              const snap = readDomFieldOrder(root);
              fieldDragStartRef.current = {
                orderIds: snap.ids,
                groups: snap.groups,
              };
            },
            onEnd: (_evt: SortableEvent) => {
              commitFieldsFromDom(root, instances);
              instances.length = 0;
            },
          }),
        );
      }

      return () => {
        for (const instance of instances) instance.destroy();
      };
    }, [
      isReadOnly,
      isCustom,
      fieldDragUnlocked,
      fields,
      groups,
      fieldSearch,
      sortableEpoch,
      commitFieldsFromDom,
    ]);

    // Group panel reorder (custom edit only).
    useEffect(() => {
      const root = rootRef.current;
      if (!root || isReadOnly || !isCustom) return;

      const groupList = root.querySelector<HTMLElement>('[data-group-list]');
      if (!groupList) return;

      const unlocked = Array.from(groupList.children).some(
        (child) =>
          child instanceof HTMLElement &&
          child.dataset.sortableKind === 'group' &&
          child.dataset.sortableLocked !== 'true',
      );
      if (!unlocked) return;

      const instance = createSortableList(groupList, {
        handle: '.secret-group-drag-handle',
        draggable: '[data-sortable-kind="group"]',
        filter: '[data-sortable-locked="true"]',
        ghostClass: 'sortable-ghost',
        chosenClass: 'sortable-chosen',
        dragClass: 'sortable-drag',
        fallbackClass: 'sortable-fallback',
        onStart: () => {
          draggingRef.current = true;
          setDraggingGroup(true);
          groupDragStartRef.current = Array.from(groupList.children)
            .filter(
              (child): child is HTMLElement =>
                child instanceof HTMLElement && child.dataset.sortableKind === 'group',
            )
            .map((child) => child.dataset.sortableId!)
            .filter(Boolean);
        },
        onEnd: () => {
          commitGroupsFromDom(groupList, instance);
        },
      });

      return () => {
        instance.destroy();
      };
    }, [isReadOnly, isCustom, groups, sortableEpoch, commitGroupsFromDom]);

    const handleSave = useCallback(() => {
      const keyToSend =
        isChangingKey && oldSecretKey && newSecretKey && newSecretKey === confirmSecretKey
          ? newSecretKey
          : undefined;
      onSave?.(
        buildData(name, fields, groups),
        keyToSend,
        isChangingKey ? oldSecretKey : undefined,
        isChangingKey ? newHint.trim() || undefined : undefined,
      );
    }, [
      isChangingKey,
      oldSecretKey,
      newSecretKey,
      confirmSecretKey,
      onSave,
      buildData,
      name,
      fields,
      groups,
      newHint,
    ]);

    const resetFromInitial = useCallback(() => {
      setName(initialData?.name || '');
      setFields(sortByOrder(initialData?.fields ?? []));
      setGroups(sortByOrder(initialData?.groups ?? []));
      setActiveGroupId(initialData?.groups?.[0]?.id);
      setIsChangingKey(false);
      setOldSecretKey('');
      setNewSecretKey('');
      setConfirmSecretKey('');
      setNewHint('');
    }, [initialData]);

    const handleDiscard = useCallback(() => {
      resetFromInitial();
      onModeChange?.('view');
    }, [resetFromInitial, onModeChange]);

    useImperativeHandle(
      ref,
      () => ({
        save: handleSave,
        discard: handleDiscard,
      }),
      [handleSave, handleDiscard],
    );

    const handleEdit = () => {
      onModeChange?.('edit');
    };

    const handleCancelEdit = () => {
      handleDiscard();
      onCancel?.();
    };

    const secretKeyMismatch =
      isChangingKey &&
      newSecretKey !== '' &&
      confirmSecretKey !== '' &&
      newSecretKey !== confirmSecretKey;

    const fieldIndexById = useMemo(() => {
      const map = new Map<string, number>();
      fields.forEach((f, i) => map.set(f.id, i));
      return map;
    }, [fields]);

    const visibleFields = useMemo(
      () => fields.filter((field) => fieldMatchesQuery(field, fieldSearch)),
      [fields, fieldSearch],
    );

    const orderedGroups = useMemo(() => sortByOrder(groups), [groups]);

    const fieldsByGroup = useMemo(() => {
      const map = new Map<string, FileSecretFieldType[]>();
      for (const group of orderedGroups) {
        map.set(group.id, []);
      }
      const ungrouped: FileSecretFieldType[] = [];
      for (const field of sortByOrder(visibleFields)) {
        if (field.groupId && map.has(field.groupId)) {
          map.get(field.groupId)!.push(field);
        } else {
          ungrouped.push(field);
        }
      }
      return { map, ungrouped };
    }, [orderedGroups, visibleFields]);

    const removableCount = fields.filter((f) => !f.locked).length;
    const canRemoveAny = fields.length > 1;
    const websiteCount = fields.filter((f) => f.key === 'website').length;
    const groupDragEnabled = !isReadOnly && isCustom;
    // Drag only useful with 2+ fields (reorder) or any group (move into/between).
    const fieldDragUseful = fields.length > 1 || groups.length > 0;
    const firstFieldId = useMemo(() => sortByOrder(fields)[0]?.id, [fields]);

    const renderField = (field: FileSecretFieldType, group: SecretFileGroup | undefined) => {
      const index = fieldIndexById.get(field.id) ?? 0;
      const isLastWebsite = field.key === 'website' && websiteCount <= 1;
      const canRemove =
        canRemoveAny && !field.locked && !isLastWebsite && (removableCount > 0 || !field.locked);
      const canReorder =
        !isReadOnly && fieldDragUnlocked && fieldDragUseful && canReorderFieldInGroup(field, group);

      const dragHandle = canReorder ? (
        <DragHandle
          ariaLabel={t('secret.dragToReorderField', { label: field.label })}
          testId={`secret-field-drag-${field.id}`}
          handleClassName="secret-field-drag-handle"
        />
      ) : null;

      return (
        <div
          key={field.id}
          data-sortable-id={field.id}
          data-sortable-kind="field"
          data-sortable-locked={canReorder ? 'false' : 'true'}
        >
          <FileSecretField
            field={field}
            mode={mode}
            onChange={(updated) => handleFieldChange(index, updated)}
            onRemove={canRemove ? () => handleRemoveField(index) : undefined}
            autoFocus={autoFocusFirstField && field.id === firstFieldId}
            dragHandle={dragHandle}
          />
        </div>
      );
    };

    const renderFieldList = (
      groupFields: FileSecretFieldType[],
      group: SecretFileGroup | undefined,
      options?: { containerId?: string; emptyUngrouped?: boolean },
    ) => {
      const containerId =
        options?.containerId ?? (group ? `secret-fields-${group.id}` : 'secret-fields-ungrouped');
      const emptyUngrouped = options?.emptyUngrouped ?? false;
      // Empty ungrouped sits after groups — min-h strip so Sortable can drop below last group.
      const className = emptyUngrouped
        ? 'min-h-8'
        : groupFields.length === 0
          ? 'min-h-8'
          : 'flex flex-col gap-3';

      return (
        <div
          data-field-list
          data-group-parent={group?.id ?? ''}
          data-testid={containerId}
          className={className}
        >
          {groupFields.map((field) => renderField(field, group))}
        </div>
      );
    };

    const renderGroupPanel = (group: SecretFileGroup, options?: { embedFieldDnd?: boolean }) => {
      const embedFieldDnd = options?.embedFieldDnd ?? false;
      const groupFields = fieldsByGroup.map.get(group.id) ?? [];
      if (groupFields.length === 0 && fieldSearch.trim()) return null;
      const isAutofillGroup = group.key === 'credentials_autofill';
      const isCustomFieldsGroup = group.key === 'custom_fields';
      const canEditGroup = canReorderGroup(group);
      const canDelete = canEditGroup && groupFields.length === 0;
      const dragEnabled = canEditGroup;

      const header = (
        <>
          {canEditGroup ? (
            <Input
              value={group.label}
              onChange={(e) => handleRenameGroup(group.id, e.target.value)}
              onClick={(e) => e.stopPropagation()}
              size="small"
              variant="borderless"
              className="min-w-0 flex-1 px-0! text-xs font-semibold tracking-wide"
              aria-label={t('secret.renameGroup')}
              data-testid={`secret-group-rename-${group.id}`}
            />
          ) : (
            <div className="text-gray-dark min-w-0 flex-1 px-1 text-xs font-semibold tracking-wide">
              {group.label}
            </div>
          )}
          {canDelete && (
            <Tooltip title={t('secret.deleteGroup')}>
              <Button
                type="text"
                size="small"
                danger
                icon={<Icon name="TrashIcon" className="h-3.5 w-3.5" />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteGroup(group.id);
                }}
                data-testid={`secret-group-delete-${group.id}`}
                aria-label={t('secret.deleteGroup')}
              />
            </Tooltip>
          )}
        </>
      );

      const fieldList = embedFieldDnd
        ? renderFieldList(groupFields, group, { containerId: groupContainerId(group.id) })
        : renderFieldList(groupFields, group);

      const body = (
        <>
          {fieldList}
          {groupFields.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('secret.noFieldsYet')} />
          ) : null}
          {!isReadOnly && isCredentials && isAutofillGroup && (
            <Button
              type="dashed"
              block
              className="mt-3"
              icon={<Icon name="CirclePlusIcon" className="h-4 w-4" />}
              onClick={(e) => {
                e.stopPropagation();
                handleAddWebsite();
              }}
              data-testid="secret-add-website"
            >
              {t('secret.addWebsite')}
            </Button>
          )}
          {!isReadOnly && !isCustom && isCustomFieldsGroup && (
            <Button
              type="dashed"
              block
              className="mt-3"
              icon={<Icon name="CirclePlusIcon" className="h-4 w-4" />}
              onClick={(e) => {
                e.stopPropagation();
                handleAddField();
              }}
              data-testid="secret-add-field-in-group"
            >
              {t('secret.addField')}
            </Button>
          )}
          {!isReadOnly && isCustom && (
            <Button
              type="dashed"
              block
              className="mt-3"
              icon={<Icon name="CirclePlusIcon" className="h-4 w-4" />}
              onClick={(e) => {
                e.stopPropagation();
                addFieldToGroup(group.id);
              }}
              data-testid={`secret-add-field-in-group-${group.id}`}
            >
              {t('secret.addFieldForGroup')}
            </Button>
          )}
        </>
      );

      if (groupDragEnabled) {
        return (
          <GroupPanelShell
            key={group.id}
            group={group}
            onActivate={() => !isReadOnly && setActiveGroupId(group.id)}
            dragHandle={
              dragEnabled ? (
                <DragHandle
                  ariaLabel={t('secret.dragToReorderGroup', { label: group.label })}
                  testId={`secret-group-drag-${group.id}`}
                  handleClassName="secret-group-drag-handle"
                />
              ) : null
            }
            header={header}
          >
            {body}
          </GroupPanelShell>
        );
      }

      return (
        <GroupPanelShell
          key={group.id}
          group={group}
          onActivate={() => !isReadOnly && setActiveGroupId(group.id)}
          dragHandle={null}
          header={header}
        >
          {body}
        </GroupPanelShell>
      );
    };

    const renderUngroupedSection = (embedFieldDnd: boolean) => {
      const ungrouped = fieldsByGroup.ungrouped;
      const showEmptyDropZone = embedFieldDnd && !isReadOnly && !fieldSearch.trim();
      if (ungrouped.length === 0 && !showEmptyDropZone) return null;

      const empty = ungrouped.length === 0;
      return (
        <div
          className={empty ? undefined : 'flex flex-col gap-3'}
          data-testid="secret-ungrouped-section"
          data-empty={empty ? 'true' : 'false'}
          onClick={() => !isReadOnly && setActiveGroupId(undefined)}
        >
          {embedFieldDnd
            ? renderFieldList(ungrouped, undefined, {
                containerId: UNGROUPED_CONTAINER_ID,
                emptyUngrouped: empty,
              })
            : ungrouped.length > 0
              ? renderFieldList(ungrouped, undefined)
              : null}
        </div>
      );
    };

    // Top-level visual: groups first, ungrouped last (append / drop-below-group).
    const groupsContent = (
      <div className="flex flex-col gap-4">
        {orderedGroups.map((group) => renderGroupPanel(group))}
        {renderUngroupedSection(false)}
      </div>
    );

    const customEditContent = (
      <div
        ref={rootRef}
        data-secret-dnd
        data-testid="secret-custom-dnd"
        className="flex flex-col gap-4"
      >
        <div data-group-list className="flex flex-col gap-4">
          {orderedGroups.map((group) => renderGroupPanel(group, { embedFieldDnd: true }))}
        </div>
        {renderUngroupedSection(true)}
      </div>
    );

    const nonCustomRoot = (
      <div ref={rootRef} data-secret-dnd>
        {groups.length === 0 ? renderFieldList(visibleFields, undefined) : groupsContent}
      </div>
    );

    return (
      <div className="flex h-full min-h-0 flex-col gap-3" data-testid="secret-editor">
        {showHeader && (
          <div className="flex items-center justify-between gap-2">
            <Input
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder={isReadOnly ? t('secret.untitled') : t('secret.fileName')}
              readOnly={isReadOnly}
              variant="outlined"
              className="max-w-md text-xl font-semibold"
              styles={{
                input: {
                  fontFamily: 'inherit',
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  lineHeight: '1.75rem',
                  cursor: isReadOnly ? 'default' : undefined,
                },
              }}
              aria-label={t('secret.fileName')}
              data-testid="secret-editor-title"
            />
            {showHeaderActions && (
              <div className="flex shrink-0 gap-2">
                {isReadOnly ? (
                  onModeChange && (
                    <Button
                      type="primary"
                      icon={<Icon name="PencilIcon" className="h-4 w-4" />}
                      onClick={handleEdit}
                    >
                      {t('secret.edit')}
                    </Button>
                  )
                ) : (
                  <>
                    <Button onClick={handleCancelEdit} disabled={isSaving}>
                      {t('secret.cancel')}
                    </Button>
                    <Button
                      type="primary"
                      onClick={handleSave}
                      loading={isSaving}
                      disabled={secretKeyMismatch || fields.length === 0}
                    >
                      {t('secret.save')}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {showFieldSearch && (
          <div className="shrink-0">
            <Input
              allowClear
              className="w-full"
              placeholder={t('secret.searchFields')}
              value={fieldSearch}
              onChange={(e) => setFieldSearch(e.target.value)}
              prefix={<Icon name="SearchIcon" className="text-gray-dark h-4 w-4" />}
              data-testid="secret-field-search"
            />
          </div>
        )}

        <div className="flex min-h-0 flex-1 flex-col">
          <div
            className="mb-2 flex shrink-0 items-center justify-between gap-2"
            data-testid="secret-fields-header"
          >
            <h4 className="text-gray-dark mb-0 text-sm font-medium">
              {t('secret.fields.heading')}
            </h4>
            {!isReadOnly && isCustom && (
              <Button
                type="link"
                size="small"
                className="h-auto! px-1! py-0!"
                icon={<Icon name="CirclePlusIcon" className="h-3.5 w-3.5" />}
                onClick={handleAddGroup}
                data-testid="secret-add-group"
              >
                {t('secret.addGroup')}
              </Button>
            )}
          </div>
          <div className="min-h-0 flex-1 overflow-auto" data-testid="secret-fields-box">
            {isCustom && !isReadOnly ? (
              fields.length === 0 && groups.length === 0 ? (
                <div className="flex h-full min-h-16 items-center justify-center py-2">
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t('secret.noFieldsYet')}
                    data-testid="secret-fields-empty"
                  />
                </div>
              ) : (
                customEditContent
              )
            ) : fields.length === 0 && groups.length === 0 ? (
              <div className="flex h-full min-h-16 items-center justify-center py-2">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t('secret.noFieldsYet')}
                  data-testid="secret-fields-empty"
                />
              </div>
            ) : (
              nonCustomRoot
            )}
          </div>

          {!isReadOnly && isEncryptedFile && (
            <div
              className="border-gray-medium mt-3 rounded-lg border bg-white p-3"
              data-testid="secret-change-key-section"
            >
              {isChangingKey ? (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{t('secret.changeSecretKey')}</span>
                    <Tooltip title={t('secret.cancel')}>
                      <span
                        onClick={() => {
                          setIsChangingKey(false);
                          setOldSecretKey('');
                          setNewSecretKey('');
                          setConfirmSecretKey('');
                          setNewHint('');
                        }}
                        className="text-gray-dark hover:text-red flex cursor-pointer items-center transition-colors"
                      >
                        <Icon name="XIcon" className="h-4 w-4" />
                      </span>
                    </Tooltip>
                  </div>
                  <Input.Password
                    value={oldSecretKey}
                    onChange={(e) => setOldSecretKey(e.target.value)}
                    placeholder={t('secret.currentSecretKey')}
                    size="small"
                    autoFocus
                  />
                  <Input.Password
                    value={newSecretKey}
                    onChange={(e) => setNewSecretKey(e.target.value)}
                    placeholder={t('secret.newSecretKey')}
                    size="small"
                  />
                  <Input.Password
                    value={confirmSecretKey}
                    onChange={(e) => setConfirmSecretKey(e.target.value)}
                    placeholder={t('secret.confirmSecretKey')}
                    size="small"
                    status={secretKeyMismatch ? 'error' : undefined}
                  />
                  {secretKeyMismatch && (
                    <span className="text-red text-xs">{t('secret.secretKeyMismatch')}</span>
                  )}
                  <Input
                    value={newHint}
                    onChange={(e) => setNewHint(e.target.value)}
                    placeholder={t('secret.hintPlaceholder')}
                    size="small"
                    maxLength={100}
                  />
                </div>
              ) : (
                <Button
                  type="dashed"
                  size="small"
                  icon={<Icon name="KeyIcon" className="h-4 w-4" />}
                  onClick={() => setIsChangingKey(true)}
                >
                  {t('secret.changeSecretKey')}
                </Button>
              )}
            </div>
          )}

          {!isReadOnly && isCustom && (
            <div className="shrink-0 py-1" data-testid="secret-add-field-bar">
              <Button
                type="dashed"
                block
                icon={<Icon name="CirclePlusIcon" className="h-4 w-4" />}
                onClick={handleAddField}
                data-testid="secret-add-field"
              >
                {t('secret.addField')}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default FileViewerSecretEditor;
